import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardBody,
  Heading,
  SimpleGrid,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  Textarea,
  VStack,
  Select,
  useColorModeValue
} from '@chakra-ui/react';
import TourModeActionButton from './TourModeActionButton';
import { formatOrganizationSurveyTitle } from '../../utils/organizationSurveyHelper';

const OrganizationSurveyForm = ({
  onSubmit,
  initialData = {},
  isNew = false,
  isHub = false,
  availablePeriods = [],
  availableSurveys = [],
  isSubmitting = false,
  onToggleLock = () => {},
  isLocked = false,
  isTour = false,
}) => {
  const [formData, setFormData] = useState({
    survey_id: '',
    survey_period_id: '',
    start_date: '',
    end_date: '',
    opening_message: '',
    closing_message: '',
    roles: '',
    passcode: '',
    endpoint: ''
  });

  useEffect(() => {
    const defaultStart = new Date();
    defaultStart.setDate(defaultStart.getDate() + 1);
    const defaultStartDate = defaultStart.toISOString().split('T')[0];
  
    const defaultEnd = new Date(defaultStart);
    defaultEnd.setDate(defaultEnd.getDate() + 14);
    const defaultEndDate = defaultEnd.toISOString().split('T')[0];
  
    setFormData((prev) => ({
      ...prev,
      ...initialData,
      start_date: initialData?.start_date || defaultStartDate,
      end_date: initialData?.end_date || defaultEndDate,
    }));
  }, [initialData]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const generateRandomEndpoint = () => {
    const characters = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let result = '';
    for (let i = 0; i < 12; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    result = `${result.slice(0, 4)}-${result.slice(4, 8)}-${result.slice(8, 12)}`;
    handleChange('endpoint', result);
  };

  const cardBg = useColorModeValue('white', 'gray.800');
  const sectionBg = useColorModeValue('gray.50', 'gray.700');

  const InfoItem = ({ label, value }) => (
    <Box>
      <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={1}>
        {label}
      </Text>
      <Text fontSize="sm" color="gray.800">
        {value || 'N/A'}
      </Text>
    </Box>
  );

  return (
    <VStack spacing={6} align="stretch" bg={sectionBg} p={4} borderRadius="md">
      {isHub && (
        <Card bg={cardBg} shadow="md" borderRadius="lg" mb={6}>
          <CardHeader pb={0}>
            <Heading as="h4" size="sm">
              Survey Info
            </Heading>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
              <InfoItem label="Survey" value={formatOrganizationSurveyTitle(formData)} />
              <InfoItem label="Endpoint" value={formData?.endpoint} />
              <InfoItem
                label="Period"
                value={formData?.survey_period?.name || formData?.survey_period?.key}
              />
            </SimpleGrid>
          </CardBody>
        </Card>
      )}
      
      {isHub && isLocked && (
        <Box
          bg="yellow.50"
          border="1px"
          borderColor="yellow.300"
          borderRadius="md"
          p={4}
          mb={4}
        >
          <Text fontWeight="medium" color="yellow.800">
            This survey is currently locked and cannot be edited. To make changes or reactivate this survey, please contact the HKE team.
          </Text>
        </Box>
      )}

      {!isHub && (
        <Box mb={4}>
          <Box
            bg={isLocked ? 'red.50' : 'green.50'}
            border="1px"
            borderColor={isLocked ? 'red.300' : 'green.300'}
            borderRadius="md"
            p={4}
            mb={4}
          >
            <Text fontWeight="bold" mb={2}>
              {isLocked
                ? 'This survey is currently locked. Unlock it to allow changes.'
                : 'This survey is currently editable. Lock it to prevent further changes.'}
            </Text>
            <TourModeActionButton
              colorScheme={isLocked ? 'red' : 'green'}
              size="sm"
              onClick={onToggleLock}
              variant="outline"
              isTour={isTour}
            >
              {isLocked ? 'Unlock Survey' : 'Lock Survey'}
            </TourModeActionButton>
          </Box>
        </Box>
      )}

      {!isHub && (
        <>
          <FormControl isRequired>
            <FormLabel>Survey</FormLabel>
            <Select
              placeholder="Select a survey"
              value={formData.survey_id}
              onChange={(e) => handleChange('survey_id', e.target.value)}
            >
              {availableSurveys.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.language})
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Survey Period</FormLabel>
            <Select
              placeholder="Select a period"
              value={formData.survey_period_id}
              onChange={(e) => handleChange('survey_period_id', e.target.value)}
            >
              {availablePeriods.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name || p.key}
                </option>
              ))}
            </Select>
          </FormControl>
        </>
      )}

      {!isHub && (
        <FormControl>
          <FormLabel>Endpoint</FormLabel>
          <InputGroup size="md">
            <Input
              type="text"
              value={formData.endpoint}
              onChange={(e) => handleChange('endpoint', e.target.value)}
              placeholder="Enter or generate an endpoint"
              fontWeight="bold"
            />
            <InputRightElement width="6rem">
              <TourModeActionButton size="sm" colorScheme="blue" variant="outline" onClick={generateRandomEndpoint} isTour={isTour}>
                Generate
              </TourModeActionButton>
            </InputRightElement>
          </InputGroup>
        </FormControl>
      )}

      <FormControl>
        <FormLabel>Passcode</FormLabel>
        <Input
          type="text"
          value={formData.passcode}
          onChange={(e) => handleChange('passcode', e.target.value)}
          placeholder="Enter a passcode"
          fontWeight="bold"
        />
      </FormControl>

      <FormControl>
        <FormLabel>Start Date</FormLabel>
        <Input
          type="date"
          value={formData.start_date}
          onChange={(e) => handleChange('start_date', e.target.value)}
        />
      </FormControl>

      <FormControl>
        <FormLabel>End Date</FormLabel>
        <Input
          type="date"
          value={formData.end_date}
          onChange={(e) => handleChange('end_date', e.target.value)}
        />
      </FormControl>

      <FormControl>
        <FormLabel>Opening Message</FormLabel>
        <Textarea
          value={formData.opening_message}
          onChange={(e) => handleChange('opening_message', e.target.value)}
          placeholder="Message shown before survey starts"
        />
      </FormControl>

      <FormControl>
        <FormLabel>Closing Message</FormLabel>
        <Textarea
          value={formData.closing_message}
          onChange={(e) => handleChange('closing_message', e.target.value)}
          placeholder="Message shown after survey is completed"
        />
      </FormControl>

      <FormControl>
        <FormLabel>Teams</FormLabel>
        <Textarea
          placeholder="Enter teams separated by commas"
          value={formData.roles}
          onChange={(e) => handleChange('roles', e.target.value)}
        />
      </FormControl>

      <TourModeActionButton
        type="submit"
        colorScheme="green"
        variant="outline"
        isTour={isTour}
        isLoading={isSubmitting}
        isDisabled={isLocked}
        onClick={(e) => {
          e.preventDefault();
          onSubmit(formData);
        }}
      >
        {isNew ? 'Assign Survey' : 'Save Changes'}
      </TourModeActionButton>
    </VStack>
  );
};

export default OrganizationSurveyForm;
